import { Injectable, inject } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, finalize, of, tap, throwError } from 'rxjs';
import { LoaderService } from '../services/loader.service';
import { CacheEndpoint } from '../constants/api-cache-config';
import { ConstantVariables } from '@app/shared/util/constant-variables';

@Injectable()
export class LoaderInterceptor implements HttpInterceptor {
  private loadingService = inject(LoaderService);
  private constantVar = inject(ConstantVariables)
  cacheableEndpoints = CacheEndpoint;
  apiCache = new Map<string, HttpResponse<any> | HttpErrorResponse>();

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const miscReq = request.url.endsWith('subject-classification-page-access.json');
    const hideLoader = request.params.has('hideLoader') || miscReq;
    const fromResolver = request.params.has('resolver');
    const rbacJson = request.url.endsWith('access.json');
    const skipCall = request.url.includes('/user/') || (rbacJson && !miscReq) || (['POST', 'PUT', 'PATCH'].some(verb => request.method.includes(verb)) && !request.url.includes('courseGroups'));
    let overlayOpened = false;
    const urlPath = request.url.split('v1/')[1];

    if (hideLoader) request = request.clone({ params: request.params.delete('hideLoader') });
    if (fromResolver) request = request.clone({ params: request.params.delete('resolver') });

    if (['POST', 'PUT', 'PATCH'].includes(request.method) && (this.constantVar.instanceDetailsRegex.test(urlPath) || this.constantVar.courseDetailsRegex.test(urlPath) || this.constantVar.institutionDetailsRegex.test(urlPath)))
      this.apiCache.delete(request.urlWithParams)

    if (request.method === 'GET' && this.apiCache.has(request.urlWithParams)) {
      const cachedResponse = this.apiCache.get(request.urlWithParams);

      if (!this.cacheableEndpoints.includes(request.urlWithParams) && !rbacJson) this.apiCache.delete(request.urlWithParams);

      if (cachedResponse instanceof HttpResponse)
        return of(new HttpResponse({
          body: structuredClone(cachedResponse.body),
          headers: cachedResponse.headers,
          status: cachedResponse.status,
          statusText: cachedResponse.statusText,
          url: cachedResponse.url ?? ''
        }));

      if (cachedResponse instanceof HttpErrorResponse) {
        this.apiCache.delete(request.urlWithParams);
        return throwError(() => cachedResponse);
      }
    }

    if (!hideLoader) {
      this.loadingService.openOverlay(skipCall);
      overlayOpened = true;
    }

    return next.handle(request).pipe(
      tap(event => {
        if (event instanceof HttpResponse && request.method === 'GET' && (this.cacheableEndpoints.includes(request.urlWithParams) || fromResolver))
          this.apiCache.set(request.urlWithParams, new HttpResponse({
            body: structuredClone(event.body),
            headers: event.headers,
            status: event.status,
            statusText: event.statusText,
            url: event.url ?? ''
          }));
        else if (event instanceof HttpResponse && ['PUT', 'POST'].includes(request.method) && event?.body?.validationCheck === 'failed')
          this.loadingService.forceCloseOverlay();
      }),
      catchError((error: any) => {
        if (request.method === 'GET' && (this.cacheableEndpoints.includes(request.urlWithParams) || fromResolver))
          this.apiCache.set(request.urlWithParams, error);
        if (skipCall && this.loadingService.overlayRef) {
          this.loadingService.overlayRef.dispose();
          this.loadingService.overlayRef = null;
          this.loadingService.requestCounter = 0;
        }
        return throwError(() => error);
      }),
      finalize(() => {
        if (overlayOpened && !skipCall) this.loadingService.closeOverlay();
      })
    );
  }
}